import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Trash2, Eye, ExternalLink } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';

interface FlaggedRelic {
  id: string;
  content: string;
  image_url: string;
  thumbnail_url: string;
  created_at: string;
  saved_at: string;
  moderation_status: string;
  is_mature: boolean;
  user_id: string;
  profiles: {
    username: string;
    display_name: string;
  } | null;
}

const FlaggedRelicsManager: React.FC = () => {
  const [flaggedRelics, setFlaggedRelics] = useState<FlaggedRelic[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchFlaggedRelics = async () => {
    try {
      const { data, error } = await supabase
        .from('bulletins')
        .select(`
          id,
          content,
          image_url,
          thumbnail_url,
          created_at,
          saved_at,
          moderation_status,
          is_mature,
          user_id,
          profiles!bulletins_user_id_fkey (
            username,
            display_name
          )
        `)
        .eq('is_relic', true)
        .eq('moderation_status', 'flagged')
        .order('saved_at', { ascending: false });

      if (error) {
        console.error('Error fetching flagged relics:', error);
        toast({
          title: "Error",
          description: "Failed to fetch flagged relics",
          variant: "destructive"
        });
      } else {
        // Transform the data to match our interface
        const transformedData = (data || []).map(item => ({
          ...item,
          profiles: Array.isArray(item.profiles) ? item.profiles[0] : item.profiles
        }));
        setFlaggedRelics(transformedData);
      }
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const approveRelic = async (relicId: string) => {
    try {
      const { error } = await supabase
        .from('bulletins')
        .update({ moderation_status: 'approved', is_mature: false })
        .eq('id', relicId);

      if (error) {
        console.error('Error approving relic:', error);
        toast({
          title: "Error",
          description: "Failed to approve relic",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Success",
          description: "Relic approved successfully"
        });
        fetchFlaggedRelics(); // Refresh list
      }
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive"
      });
    }
  };

  const deleteRelic = async (relicId: string) => {
    if (!confirm('Are you sure you want to delete this relic? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('bulletins')
        .delete()
        .eq('id', relicId);

      if (error) {
        console.error('Error deleting relic:', error);
        toast({
          title: "Error",
          description: "Failed to delete relic",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Success",
          description: "Relic deleted successfully"
        });
        fetchFlaggedRelics(); // Refresh list
      }
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    fetchFlaggedRelics();
  }, []);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Flagged Relics Management</CardTitle>
        </CardHeader>
        <CardContent>
          <p>Loading flagged relics...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          Flagged Relics Management
          <Badge variant="destructive">{flaggedRelics.length} flagged</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {flaggedRelics.length === 0 ? (
          <p className="text-muted-foreground">No flagged relics found.</p>
        ) : (
          <div className="space-y-4">
            {flaggedRelics.map((relic) => (
              <div key={relic.id} className="border rounded-lg p-4">
                <div className="flex gap-4">
                  {/* Image */}
                  <div className="flex-shrink-0">
                    <img
                      src={relic.thumbnail_url || relic.image_url}
                      alt="Relic content"
                      className="w-20 h-20 object-cover rounded"
                    />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium">
                          @{relic.profiles?.username || 'Unknown'}
                          {relic.profiles?.display_name && (
                            <span className="text-muted-foreground ml-2">
                              ({relic.profiles.display_name})
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {relic.content}
                        </p>
                        <div className="flex gap-2 mt-2">
                          <Badge variant="secondary">
                            Created: {format(new Date(relic.created_at), 'MMM d, yyyy')}
                          </Badge>
                          <Badge variant="secondary">
                            Became relic: {format(new Date(relic.saved_at), 'MMM d, yyyy')}
                          </Badge>
                          <Badge variant="destructive">Flagged</Badge>
                          {relic.is_mature && <Badge variant="outline">Mature</Badge>}
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(`/bulletins/relics/${relic.id}`, '_blank')}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(relic.image_url, '_blank')}
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          Image
                        </Button>
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => approveRelic(relic.id)}
                        >
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => deleteRelic(relic.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FlaggedRelicsManager;